import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, delay, map, Observable, of, throwError } from 'rxjs';
import { AuthStore } from '@navstation-portal/shared/data-access/auth';
import {
  DeleteReportResponse,
  EdnFileType,
  ReportApiService,
  SWELL_OPTIONS,
} from '@navstation-portal/reporting/data-access/vessel-report';

import {
  DisplacementResponse,
  EngineMode,
  MeRpmPowerResponse,
  OTHER_SPEED_STEAMING_OPTIONS,
  PortCallPurpose,
  TcRpmPowerResponse,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  FieldType,
  FormFieldConfig,
  GroupedFormData,
} from '@navstation-portal/reporting/data-model/ui';
import { ReportFormRuleTemplate } from '@navstation-portal/reporting/data-model/ui';
import {
  ApiErrorMapper,
  ApiServiceError,
} from '@navstation-portal/shared/util/api';
import {
  DropboxItem,
  SelectOption,
} from '@navstation-portal/shared/data-model/common';
import { TIMEZONE_SELECT_OPTIONS } from '@navstation-portal/shared/util/time';
import { environment } from '../../environments/environment';
import { TvaApiReport } from './report-adapter.service';

export interface SaveReportResponse {
  report_id: string;
  report_number: number;
  report?: TvaApiReport;
}

// The API wraps successful responses as { code, data }.
interface ApiEnvelope<T> {
  code: number;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class ReportService implements ReportApiService {
  private http = inject(HttpClient);
  private authStore = inject(AuthStore);
  private apiError = inject(ApiErrorMapper).forFeature('Report');

  getFormFieldRules(): Observable<Record<string, GroupedFormData>> {
    return of(1).pipe(
      map(() => mockFormData),
      delay(1000),
    );
  }

  getReportFormRuleTemplate(
    vesselId: string,
  ): Observable<ReportFormRuleTemplate> {
    return this.http
      .get<ReportFormRuleTemplate>(
        `${environment.BASE_URL}/${vesselId}/reportFormRuleTemplate`,
      )
      .pipe(
        catchError(this.apiError.toApiError('getting the form rule template')),
      );
  }

  // Returns an error rather than throwing, so callers stay observable-based and
  // must `return throwError(...)` — a bare guard call would be a no-op.
  private notLoggedInError(): ApiServiceError | null {
    return this.authStore.isLoggedIn()
      ? null
      : Object.assign(new Error('Not logged in.'), { status: 401 });
  }

  getReport(vesselId: string, reportId: string): Observable<TvaApiReport> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId || !reportId) {
      return throwError(() => new Error('vesselId and reportId are required.'));
    }

    return this.http
      .get<TvaApiReport>(
        `${environment.BASE_URL}/${vesselId}/reports/${reportId}`,
      )
      .pipe(catchError(this.apiError.toApiError('getting the report')));
  }

  saveReport(
    vesselId: string,
    report: TvaApiReport,
    reportId?: string,
    submitType?: string,
  ): Observable<SaveReportResponse> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId) {
      return throwError(() => new Error('vesselId is required.'));
    }

    // Submit uses the same route as save, with query params flagging the
    // submission and its type (only 'send' exists today).
    const query = submitType ? `?submit=true&submitType=${submitType}` : '';
    const baseUrl = `${environment.BASE_URL}/${vesselId}/reports`;
    const request$ = reportId
      ? this.http.put<ApiEnvelope<SaveReportResponse>>(
          `${baseUrl}/${reportId}${query}`,
          report,
        )
      : this.http.post<ApiEnvelope<SaveReportResponse>>(
          `${baseUrl}${query}`,
          report,
        );

    return request$.pipe(
      map((envelope) => envelope.data),
      catchError(this.apiError.toApiError('saving the report')),
    );
  }

  deleteReport(
    vesselId: string,
    reportId: string,
  ): Observable<DeleteReportResponse> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId) {
      return throwError(() => new Error('vesselId is required.'));
    }

    return this.http
      .delete<
        ApiEnvelope<DeleteReportResponse>
      >(`${environment.BASE_URL}/${vesselId}/reports/${reportId}`)
      .pipe(
        map((envelope) => envelope.data),
        catchError(this.apiError.toApiError('deleting the report')),
      );
  }

  private ednEndpoint(ednType: EdnFileType): string {
    return ednType === 'edn' ? 'ednUpload' : 'ednProofOfSustainabilityUpload';
  }

  private ednPropertyName(ednType: EdnFileType): string {
    return ednType === 'edn'
      ? 'edn_files'
      : 'proof_of_sustainability_edn_files';
  }

  uploadEdnFile(
    vesselId: string,
    reportId: string,
    ednType: EdnFileType,
    file: File,
  ): Observable<DropboxItem[]> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId || !reportId) {
      return throwError(() => new Error('vesselId and reportId are required.'));
    }

    const formData = new FormData();
    formData.append('filedata', file, file.name);

    const url = `${environment.BASE_URL}/${vesselId}/reports/${reportId}/${this.ednEndpoint(ednType)}`;
    return this.http
      .post<ApiEnvelope<Record<string, DropboxItem[]>>>(url, formData)
      .pipe(
        map((envelope) => envelope.data[this.ednPropertyName(ednType)] ?? []),
        catchError(this.apiError.toApiError('uploading the file')),
      );
  }

  deleteEdnFile(
    vesselId: string,
    reportId: string,
    ednType: EdnFileType,
    dropboxUrl: string,
  ): Observable<DropboxItem[]> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId || !reportId) {
      return throwError(() => new Error('vesselId and reportId are required.'));
    }

    const url = `${environment.BASE_URL}/${vesselId}/reports/${reportId}/${this.ednEndpoint(ednType)}`;
    return this.http
      .delete<
        ApiEnvelope<Record<string, DropboxItem[]>>
      >(url, { params: { dropbox_url: dropboxUrl } })
      .pipe(
        map((envelope) => envelope.data[this.ednPropertyName(ednType)] ?? []),
        catchError(this.apiError.toApiError('deleting the file')),
      );
  }

  getCarryoverReport(
    vesselId: string,
    reportId?: string,
  ): Observable<TvaApiReport> {
    const authError = this.notLoggedInError();
    if (authError) {
      return throwError(() => authError);
    }

    if (!vesselId) {
      return throwError(() => new Error('vesselId is required.'));
    }

    const baseUrl = `${environment.BASE_URL}/${vesselId}/carryOverData`;
    const url = reportId
      ? `${baseUrl}?reportId=${encodeURIComponent(reportId)}`
      : baseUrl;

    return this.http
      .get<TvaApiReport>(url)
      .pipe(
        catchError(this.apiError.toApiError('getting the carryover report')),
      );
  }

  getTcRpmPower(
    vesselId: string,
    tcrpm1: number | undefined,
    tcrpm2: number | undefined,
    modes: EngineMode[] | undefined,
  ): Observable<TcRpmPowerResponse> {
    const data: Record<string, unknown> = {};
    if (tcrpm1 != null) data['tcrpm1'] = tcrpm1;
    if (tcrpm2 != null) data['tcrpm2'] = tcrpm2;
    if (modes != null) data['modes'] = modes;

    return this.http
      .post<{
        data: TcRpmPowerResponse;
      }>(`${environment.BASE_URL}/${vesselId}/tcrpmPower`, data)
      .pipe(map((response) => response.data));
  }

  getMeRpmPower(
    vesselId: string,
    merpm: number | undefined,
    modes: EngineMode[] | undefined,
  ): Observable<MeRpmPowerResponse> {
    const data: Record<string, unknown> = {};
    if (merpm != null) data['merpm'] = merpm;
    if (modes != null) data['modes'] = modes;

    return this.http
      .post<{
        data: MeRpmPowerResponse;
      }>(`${environment.BASE_URL}/${vesselId}/meRPMPower`, data)
      .pipe(map((response) => response.data));
  }

  getDisplacement(
    vesselId: string,
    draught: number,
  ): Observable<DisplacementResponse> {
    return this.http.get<DisplacementResponse>(
      `${environment.BASE_URL}/${vesselId}/displacement`,
      {
        params: { draught },
      },
    );
  }

  getPortCallPurposes(): Observable<PortCallPurpose[]> {
    return this.http
      .get<
        ApiEnvelope<{ portCallPurposes: PortCallPurpose[] }>
      >(`${environment.BASE_URL}/referenceData`)
      .pipe(
        map((envelope) => envelope.data.portCallPurposes),
        catchError(this.apiError.toApiError('getting the port call purposes')),
      );
  }
}

export const OPERATION_TYPE_OPTIONS: SelectOption[] = [
  { name: 'Loading', code: 'Loading' },
  { name: 'Discharging', code: 'Discharging' },
  { name: 'Loading and Discharging', code: 'Loading and Discharging' },
  { name: 'Idling', code: 'Idling' },
  { name: 'Dry Dock or Repair', code: 'Dry Dock or Repair' },
] as const;

export const LOCATION_OPTIONS: SelectOption[] = [
  { name: 'In Port/Berth', code: 'In Port/Berth' },
  { name: 'SBM/SPM', code: 'SBM/SPM' },
  { name: 'Ship to Ship Transfer', code: 'Ship to Ship Transfer' },
] as const;

// Shared between the `operational` and `power` mock tab entries below — Shore Power
// renders on whichever tab reportStore.shouldShowShorePowerOnOperationalTab() picks,
// so both need the identical field-rule set for isFieldRequired()/isFieldReadOnly().
const SHORE_POWER_FORM_FIELDS: FormFieldConfig[] = [
  {
    fieldLabel: 'Shore Power In Use',
    fieldId: 'isUsed',
    isRequired: false,
    fieldType: FieldType.Checkbox,
  },
  {
    fieldLabel: 'Reception Port',
    fieldId: 'receptionPort',
    isRequired: false,
    isReadOnly: true,
    fieldType: FieldType.Text,
  },
  {
    fieldLabel: 'Electrical Work',
    fieldId: 'kwh',
    isRequired: true,
    fieldType: FieldType.Number,
    fieldUnit: 'kWh',
  },
  {
    fieldLabel: 'Shore Power Duration',
    fieldId: 'hours',
    isRequired: true,
    isReadOnly: true,
    fieldType: FieldType.Number,
    fieldUnit: 'Hours',
  },
];

const mockFormData: Record<string, GroupedFormData> = {
  operational: {
    vesselDetails: [
      {
        fieldLabel: 'Report Number',
        fieldId: 'reportNumber',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Voyage Number',
        fieldId: 'voyageNumber',
        isRequired: true,
        fieldType: FieldType.Text,
      },
      {
        fieldLabel: 'Instructed Speed',
        fieldId: 'instructedSpeed',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'Instructed FOC',
        fieldId: 'instructedFoc',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'CP Speed',
        fieldId: 'cpSpeed',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'CP Fuel',
        fieldId: 'cpFuel',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'Other Speed Steaming',
        fieldId: 'otherSpeedSteaming',
        isRequired: false,
        isVisible: false,
        fieldType: FieldType.Select,
        fieldOptions: OTHER_SPEED_STEAMING_OPTIONS,
      },
    ],
    miscellaneous: [
      {
        fieldLabel: 'Captain Name',
        fieldId: 'captainName',
        isRequired: false,
        fieldType: FieldType.Text,
      },
      {
        fieldLabel: 'Chief Engineer',
        fieldId: 'chiefEngineer',
        isRequired: false,
        fieldType: FieldType.Text,
      },
      {
        // isVisible: false so form-validity.utility.ts's stampInitialStatuses treats it as always
        // 'VALID' rather than stamping it INVALID-and-stuck on non-Port reports, where this field
        // renders in cargoDetails-operational instead (see showCargoWeightInCargoDetails) and this
        // copy of it never mounts to correct that stamp. Port reports still enforce it live via the
        // template's own [required]="isFieldRequired('cargoWeight')" binding, unaffected by this flag.
        fieldLabel: 'Cargo Weight',
        fieldId: 'cargoWeight',
        isRequired: true,
        isVisible: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
    ],
    reportTime: [
      {
        fieldLabel: 'Report From',
        fieldId: 'reportFrom',
        fieldType: FieldType.Calendar,
        isRequired: true,
        isReadOnly: true,
      },
      {
        fieldLabel: 'Report To',
        fieldId: 'reportTo',
        fieldType: FieldType.Calendar,
        isRequired: true,
      },
      {
        fieldLabel: 'Report Creation',
        fieldId: 'reportCreation',
        fieldType: FieldType.Calendar,
        isReadOnly: true,
      },
      {
        fieldLabel: 'Timezone',
        fieldId: 'timezone',
        fieldType: FieldType.Select,
        fieldOptions: TIMEZONE_SELECT_OPTIONS,
        isRequired: true,
      },
      {
        fieldLabel: 'Previous Timezone',
        fieldId: 'previousTimezone',
        fieldType: FieldType.Select,
        fieldOptions: TIMEZONE_SELECT_OPTIONS,
      },
      {
        fieldLabel: 'Report Period',
        fieldId: 'reportPeriod',
        fieldType: FieldType.Number,
        fieldUnit: 'Hours',
        isReadOnly: true,
      },
    ],
    portAndPosition: [
      {
        fieldLabel: 'Port Name',
        fieldId: 'portName',
        isRequired: true,
        fieldType: FieldType.Text,
      },
      {
        fieldLabel: 'Position',
        fieldId: 'portPosition',
        fieldType: FieldType.Position,
        isRequired: true,
      },
    ],
    stabilityConditions: [
      {
        fieldLabel: 'Displacement',
        fieldId: 'displacement',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
        isReadOnly: true,
        calculated: true,
      },
      {
        // V1: hideByDefault('report.operational.gm') - hidden unless a business rule marks it visible.
        // Mocked here as visible until a real form-field-rules backend rule exists.
        fieldLabel: 'GM',
        fieldId: 'gm',
        fieldType: FieldType.Number,
        fieldUnit: 'm',
        isVisible: true,
      },
      {
        // V1: hideByDefault('report.operational.shear_force')
        fieldLabel: 'Shear Force',
        fieldId: 'shearForce',
        fieldType: FieldType.Number,
        fieldUnit: 'kN',
        isVisible: true,
      },
      {
        // V1: hideByDefault('report.operational.bending_force')
        fieldLabel: 'Bending Force',
        fieldId: 'bendingForce',
        fieldType: FieldType.Number,
        fieldUnit: 'kNm',
        isVisible: true,
      },
      {
        // V1: hideByDefault('report.operational.torsion')
        fieldLabel: 'Torsion',
        fieldId: 'torsion',
        fieldType: FieldType.Number,
        fieldUnit: 'kNm',
        isVisible: true,
      },
      {
        fieldLabel: 'GM Reserve',
        fieldId: 'gmReserve',
        fieldType: FieldType.Number,
        fieldUnit: 'cm',
      },
      {
        fieldLabel: 'Dead Weight Reserve',
        fieldId: 'deadWeightReserve',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
        // mocked visibility; V1 only showed this for PCC, PCTC, RORO and Container vessel types
        isVisible: true,
      },
      {
        // V1: showByDefault('...constant_weight') + requireByDefault(...) - visible and required unless a
        // rule says otherwise.
        fieldLabel: 'Constant Weight',
        fieldId: 'constantWeight',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
        isRequired: true,
      },
    ],
    portOperationsTimestamp: [
      {
        fieldLabel: 'ETD',
        fieldId: 'etd',
        fieldType: FieldType.Calendar,
        isRequired: true,
      },
      {
        fieldLabel: 'NOR Tendered',
        fieldId: 'norTendered',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'All Fast',
        fieldId: 'allFast',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Hoses Connected',
        fieldId: 'hosesConnected',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Commence Load',
        fieldId: 'commentLoad',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Finish Load',
        fieldId: 'finishLoad',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Hoses Disconnected',
        fieldId: 'hosesDisconnected',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Last Line',
        fieldId: 'lastLine',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Cargo Docs Onboard',
        fieldId: 'cargoDocsOnboard',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Start Discharging',
        fieldId: 'startDischarging',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'End Discharging',
        fieldId: 'endDischarging',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Completed Tank Inspection',
        fieldId: 'completedTankInspection',
        fieldType: FieldType.Calendar,
      },
    ],
    poolSpecificFields: [
      {
        fieldLabel: 'Fresh Water / Domestic',
        fieldId: 'freshWaterDomestic',
        fieldType: FieldType.Number,
        fieldUnit: 'm3',
        isRequired: true,
      },
      {
        fieldLabel: 'Fresh Water / Tank Cleaning',
        fieldId: 'freshWaterTankCleaning',
        fieldType: FieldType.Number,
        fieldUnit: 'm3',
        isRequired: true,
      },
      {
        fieldLabel: 'Slops / Residual Tanks',
        fieldId: 'slopsResidualTanks',
        fieldType: FieldType.Number,
        fieldUnit: 'm3',
        isRequired: true,
      },
      {
        fieldLabel: 'Slops / Slop Tanks',
        fieldId: 'slopsSlopTanks',
        fieldType: FieldType.Number,
        fieldUnit: 'm3',
        isRequired: true,
      },
      {
        fieldLabel: 'Total Interface / Oil',
        fieldId: 'totalInterfaceOil',
        fieldType: FieldType.Number,
        fieldUnit: 'm3',
        isRequired: true,
      },
      {
        fieldLabel: 'First Line',
        fieldId: 'firstLine',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Gangway Down',
        fieldId: 'gangwayDown',
        fieldType: FieldType.Calendar,
      },
    ],
    operatingConditions: [
      {
        fieldLabel: 'Type of Operation',
        fieldId: 'typeOfOperation',
        fieldType: FieldType.Select,
        fieldOptions: OPERATION_TYPE_OPTIONS,
        isRequired: true,
      },
      {
        fieldLabel: 'Location',
        fieldId: 'location',
        fieldType: FieldType.Select,
        fieldOptions: LOCATION_OPTIONS,
        isRequired: true,
      },
      {
        fieldLabel: 'Tugs All Fast',
        fieldId: 'tugsAllFast',
        fieldType: FieldType.Number,
        fieldUnit: 'Tugs',
      },
      {
        fieldLabel: 'Tugs Last Line',
        fieldId: 'tugsLastLine',
        fieldType: FieldType.Number,
        fieldUnit: 'Tugs',
      },
      {
        fieldLabel: 'Draught Forward',
        fieldId: 'draughtForward',
        fieldType: FieldType.Number,
        fieldUnit: 'm',
        isRequired: true,
      },
      {
        fieldLabel: 'Draught Aft',
        fieldId: 'draughtAft',
        fieldType: FieldType.Number,
        fieldUnit: 'm',
        isRequired: true,
      },
      {
        fieldLabel: 'Trim',
        fieldId: 'trim',
        fieldType: FieldType.Number,
        fieldUnit: undefined,
        isReadOnly: true,
        calculated: true,
      },
      {
        fieldLabel: 'Recommended Trim',
        fieldId: 'recommendedTrim',
        fieldType: FieldType.Number,
        fieldUnit: undefined,
        isReadOnly: false,
        isVisible: true,
      },
      {
        fieldLabel: 'Ballast Water on Board',
        fieldId: 'ballastWaterOnBoard',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
        isRequired: true,
      },
      {
        fieldLabel: 'Ballast Water Intake',
        fieldId: 'ballastWaterIntake',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'Ballast Water Discharged',
        fieldId: 'ballastWaterDischarged',
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'Ballast Water Sample Date',
        fieldId: 'ballastWaterSampleDate',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Ballast Water Count of Cells',
        fieldId: 'ballastWaterCountOfCells',
        fieldType: FieldType.Number,
        fieldUnit: 'Cells/ml',
        fieldDescription: '(10-50 micron)',
      },
    ],
    nextPort: [
      {
        fieldLabel: 'Awaiting Orders',
        fieldId: 'awaitingOrders',
        fieldType: FieldType.Checkbox,
      },
      {
        fieldLabel: 'Next Port',
        fieldId: 'nextPort',
        fieldType: FieldType.Text,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setRequired',
            value: false,
          },
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'Remaining Distance',
        fieldId: 'remainingDistance',
        fieldType: FieldType.Number,
        fieldUnit: undefined,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'Remaining Time',
        fieldId: 'remainingTime',
        fieldType: FieldType.Number,
        fieldUnit: undefined,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'Next Timezone',
        fieldId: 'nextTimezone',
        fieldType: FieldType.Select,
        fieldOptions: TIMEZONE_SELECT_OPTIONS,
      },
      {
        fieldLabel: 'ETA Pilot Station',
        fieldId: 'etaPilotStation',
        fieldType: FieldType.Calendar,
      },
    ],
    voyageItinerary: [
      {
        fieldLabel: 'Awaiting Orders',
        fieldId: 'awaitingOrders',
        fieldType: FieldType.Checkbox,
        conditions: [
          {
            when: 'arrivingAtEosp',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'Arriving at EOSP',
        fieldId: 'arrivingAtEosp',
        fieldType: FieldType.Checkbox,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'Next Port',
        fieldId: 'nextPort',
        fieldType: FieldType.Text,
        isRequired: true,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setRequired',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'ETA',
        fieldId: 'eta',
        fieldType: FieldType.Calendar,
        isRequired: true,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setRequired',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'ETA Timezone',
        fieldId: 'etaTimezone',
        fieldType: FieldType.Select,
        fieldOptions: TIMEZONE_SELECT_OPTIONS,
        isRequired: true,
        conditions: [
          {
            when: 'awaitingOrders',
            is: true,
            action: 'setEnabled',
            value: false,
          },
        ],
      },
      {
        fieldLabel: 'COSP',
        fieldId: 'cosp',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Previously in ECA',
        fieldId: 'previouslyInEca',
        fieldType: FieldType.Text,
        isReadOnly: true,
      },
      {
        fieldLabel: 'Enter ECA',
        fieldId: 'enterEca',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Exit ECA',
        fieldId: 'exitEca',
        fieldType: FieldType.Calendar,
      },
      {
        fieldLabel: 'Time in ECA',
        fieldId: 'timeInEca',
        fieldType: FieldType.Number,
        fieldUnit: 'Hours',
        isReadOnly: true,
        calculated: true,
      },
      {
        fieldLabel: 'In ECA',
        fieldId: 'inEca',
        fieldType: FieldType.Text,
        isReadOnly: true,
        calculated: true,
      },
    ],
    shorePower: SHORE_POWER_FORM_FIELDS,
  },
  positionAndWeather: {
    wind: [
      {
        fieldLabel: 'Apparent Wind Speed',
        fieldId: 'apparentWindSpeed',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'm/s',
      },
      {
        fieldLabel: 'Apparent Wind Direction',
        fieldId: 'apparentWindDirection',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg',
      },
      {
        fieldLabel: 'True Wind Reporting',
        fieldId: 'trueWindReporting',
        isRequired: false,
        fieldType: FieldType.Checkbox,
      },
      {
        fieldLabel: 'True Wind Speed',
        fieldId: 'trueWindSpeed',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'm/s',
      },
      {
        fieldLabel: 'True Wind Direction',
        fieldId: 'trueWindDirection',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg',
      },
    ],
    speedAndDistance: [
      {
        fieldLabel: 'Trip Counter GPS',
        fieldId: 'tripCounterGps',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Trip Counter Log',
        fieldId: 'tripCounterLog',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Speed Log Malfunction',
        fieldId: 'speedLogMalfunction',
        isRequired: false,
        fieldType: FieldType.Checkbox,
      },
      {
        fieldLabel: 'Log Speed',
        fieldId: 'logSpeed',
        isRequired: true, // if !speedLogMalfunction
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'Log Distance',
        fieldId: 'logDistance',
        isRequired: true, // if !speedLogMalfunction
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Course Over Ground (COG)',
        fieldId: 'courseOverGround',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Observed Speed',
        fieldId: 'observedSpeed',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'Observed Distance',
        fieldId: 'observedDistance',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Observed Distance In ECA',
        fieldId: 'observedDistanceInEca',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Observed Distance for Total Sea Passage',
        fieldId: 'observedDistanceTotalSeaPassage',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Remaining Distance',
        fieldId: 'remainingDistance',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
      {
        fieldLabel: 'Bad Weather Hours',
        fieldId: 'badWeatherHours',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Hours',
      },
      {
        fieldLabel: 'Bad Weather Observed Distance',
        fieldId: 'badWeatherObservedDistance',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'NM',
      },
    ],
    shipPosition: [
      {
        fieldLabel: 'Ship Position',
        fieldId: 'shipPosition',
        fieldType: FieldType.Position,
        isRequired: true,
      },
    ],
    currentsAndSeaWater: [
      {
        fieldLabel: 'Sea Swell',
        fieldId: 'seaSwell',
        fieldType: FieldType.Select,
        fieldOptions: SWELL_OPTIONS,
        isRequired: true,
      },
      {
        fieldLabel: 'Wave Height',
        fieldId: 'waveHeight',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'm',
      },
      {
        fieldLabel: 'Water Depth',
        fieldId: 'waterDepth',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'm',
      },
      {
        fieldLabel: 'Current Direction (True North)',
        fieldId: 'currentDirectionTrueNorth',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg',
      },
      {
        fieldLabel: 'True Current Speed',
        fieldId: 'trueCurrentSpeed',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'Apparent Current Direction',
        fieldId: 'apparentCurrentDirection',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg',
      },
      {
        fieldLabel: 'Apparent Current Speed',
        fieldId: 'apparentCurrentSpeed',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: 'Knots',
      },
      {
        fieldLabel: 'Apparent Wave Direction',
        fieldId: 'apparentWaveDirection',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg',
      },
      {
        fieldLabel: 'Sea Temperature',
        fieldId: 'seaTemperature',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'deg C',
      },
      {
        fieldLabel: 'Sea Water Density',
        fieldId: 'seaWaterDensity',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'g/ml',
      },
      {
        fieldLabel: 'Atmospheric Pressure',
        fieldId: 'atmosphericPressure',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'mBar',
      },
      {
        fieldLabel: 'Salinity',
        fieldId: 'salinity',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: 'ppm',
      },
    ],
  },
  power: {
    mainEngine: [
      {
        fieldLabel: 'Power',
        fieldId: 'power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'kW',
      },
      {
        fieldLabel: 'RPL',
        fieldId: 'rpm',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Load',
        fieldId: 'mainEngineLoad',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Running HOurs',
        fieldId: 'mainEngineRunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME RPM',
        fieldId: 'rpm',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Revolution Counter',
        fieldId: 'revCounterCurrent',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Revolution Counter Previous',
        fieldId: 'revCounterPrevious',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Revolution Counter Difference',
        fieldId: 'revCounterPrevDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Revolution Counter Difference',
        fieldId: 'mainEngineRevCounterDiff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME Calculated RPM',
        fieldId: 'mainEngineCalculatedRpm',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'TC RPM',
        fieldId: 'tcRpm1',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    machineryRunningHours: [
      {
        fieldLabel: 'Main Engine 1 Current',
        fieldId: 'mainEngine1Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Engine 1 Previous',
        fieldId: 'mainEngine1Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Engine 1 Difference',
        fieldId: 'mainEngine1Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Engine 2 Current',
        fieldId: 'mainEngine2Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Engine 2 Previous',
        fieldId: 'mainEngine2Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Engine 2 Difference',
        fieldId: 'mainEngine2Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 1 Current',
        fieldId: 'auxEngine1Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 1 Previous',
        fieldId: 'auxEngine1Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 1 Difference',
        fieldId: 'auxEngine1Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 2 Current',
        fieldId: 'auxEngine2Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 2 Previous',
        fieldId: 'auxEngine2Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 2 Difference',
        fieldId: 'auxEngine2Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 3 Current',
        fieldId: 'auxEngine3Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 3 Previous',
        fieldId: 'auxEngine3Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 3 Difference',
        fieldId: 'auxEngine3Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 4 Current',
        fieldId: 'auxEngine4Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 4 Previous',
        fieldId: 'auxEngine4Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 4 Difference',
        fieldId: 'auxEngine4Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 5 Current',
        fieldId: 'auxEngine5Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 5 Previous',
        fieldId: 'auxEngine5Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 5 Difference',
        fieldId: 'auxEngine5Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 6 Current',
        fieldId: 'auxEngine6Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 6 Previous',
        fieldId: 'auxEngine6Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 6 Difference',
        fieldId: 'auxEngine6Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 7 Current',
        fieldId: 'auxEngine7Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 7 Previous',
        fieldId: 'auxEngine7Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 7 Difference',
        fieldId: 'auxEngine7Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 8 Current',
        fieldId: 'auxEngine8Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 8 Previous',
        fieldId: 'auxEngine8Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine 8 Difference',
        fieldId: 'auxEngine8Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 1 Current',
        fieldId: 'auxBoiler1Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 1 Previous',
        fieldId: 'auxBoiler1Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 1 Difference',
        fieldId: 'auxBoiler1Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 2 Current',
        fieldId: 'auxBoiler2Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 2 Previous',
        fieldId: 'auxBoiler2Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler 2 Difference',
        fieldId: 'auxBoiler2Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 1 Current',
        fieldId: 'mainAirComp1Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 1 Previous',
        fieldId: 'mainAirComp1Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 1 Difference',
        fieldId: 'mainAirComp1Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 2 Current',
        fieldId: 'mainAirComp2Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 2 Previous',
        fieldId: 'mainAirComp2Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 2 Difference',
        fieldId: 'mainAirComp2Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 3 Current',
        fieldId: 'mainAirComp3Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 3 Previous',
        fieldId: 'mainAirComp3Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 3 Difference',
        fieldId: 'mainAirComp3Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 4 Current',
        fieldId: 'mainAirComp4Curr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 4 Previous',
        fieldId: 'mainAirComp4Prev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Main Air Compressor 4 Difference',
        fieldId: 'mainAirComp4Diff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Shaft Generator PTO Current',
        fieldId: 'shaftGeneratorPTOCurr',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Shaft Generator PTO Previous',
        fieldId: 'shaftGeneratorPTOPrev',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Shaft Generator PTO Difference',
        fieldId: 'shaftGeneratorPTODiff',
        isRequired: true,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    generatorEngines: [
      {
        fieldLabel: 'Auxiliary Engine 1 Power',
        fieldId: 'auxEngine1Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 2 Power',
        fieldId: 'auxEngine2Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 3 Power',
        fieldId: 'auxEngine3Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 4 Power',
        fieldId: 'auxEngine4Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 5 Power',
        fieldId: 'auxEngine5Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 6 Power',
        fieldId: 'auxEngine6Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 7 Power',
        fieldId: 'auxEngine7Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 8 Power',
        fieldId: 'auxEngine8Power',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 1 Max Rating',
        fieldId: 'auxEngine1MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 2 Max Rating',
        fieldId: 'auxEngine2MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 3 Max Rating',
        fieldId: 'auxEngine3MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 4 Max Rating',
        fieldId: 'auxEngine4MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 5 Max Rating',
        fieldId: 'auxEngine5MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 6 Max Rating',
        fieldId: 'auxEngine6MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 7 Max Rating',
        fieldId: 'auxEngine7MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 8 Max Rating',
        fieldId: 'auxEngine8MaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Turbo Generator',
        fieldId: 'turboMaxRating',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 1 Running Hours',
        fieldId: 'auxEngine1RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 2 Running Hours',
        fieldId: 'auxEngine2RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 3 Running Hours',
        fieldId: 'auxEngine3RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 4 Running Hours',
        fieldId: 'auxEngine4RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 5 Running Hours',
        fieldId: 'auxEngine5RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 6 Running Hours',
        fieldId: 'auxEngine6RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 7 Running Hours',
        fieldId: 'auxEngine7RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 8 Running Hours',
        fieldId: 'auxEngine8RunningHours',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 1 CAP',
        fieldId: 'auxEngine1CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 2 CAP',
        fieldId: 'auxEngine2CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 3 CAP',
        fieldId: 'auxEngine3CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 4 CAP',
        fieldId: 'auxEngine4CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 5 CAP',
        fieldId: 'auxEngine5CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 6 CAP',
        fieldId: 'auxEngine6CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 7 CAP',
        fieldId: 'auxEngine7CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 8 CAP',
        fieldId: 'auxEngine8CAP',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 1 LO Added',
        fieldId: 'auxEngine1LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 2 LO Added',
        fieldId: 'auxEngine2LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 3 LO Added',
        fieldId: 'auxEngine3LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 4 LO Added',
        fieldId: 'auxEngine4LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 5 LO Added',
        fieldId: 'auxEngine5LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 6 LO Added',
        fieldId: 'auxEngine6LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 7 LO Added',
        fieldId: 'auxEngine7LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Auxiliary Engine 8 LO Added',
        fieldId: 'auxEngine8LOAdded',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Total LO Added',
        fieldId: 'totalLoAdded',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    pumps: [
      {
        fieldLabel: 'ME LO Pump 1 Current',
        fieldId: 'meLoPump1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME LO Pump 1 Previous',
        fieldId: 'meLoPump1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME LO Pump 1 Difference',
        fieldId: 'meLoPump1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME LO Pump 2 Current',
        fieldId: 'meLoPump2Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME LO Pump 2 Previous',
        fieldId: 'meLoPump2Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ME LO Pump 2 Difference',
        fieldId: 'meLoPump2Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 1 Current',
        fieldId: 'seaWaterPump1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 1 Previous',
        fieldId: 'seaWaterPump1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 1 Difference',
        fieldId: 'seaWaterPump1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 2 Current',
        fieldId: 'seaWaterPump2Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 2 Previous',
        fieldId: 'seaWaterPump2Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Sea Water Pump 2 Difference',
        fieldId: 'seaWaterPump2Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 1 Current',
        fieldId: 'sgPump1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 1 Previous',
        fieldId: 'sgPump1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 1 Difference',
        fieldId: 'sgPump1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 2 Current',
        fieldId: 'sgPump2Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 2 Previous',
        fieldId: 'sgPump2Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SG Pump 2 Difference',
        fieldId: 'sgPump2Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 1 Current',
        fieldId: 'foPurifier1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 1 Previous',
        fieldId: 'foPurifier1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 1 Difference',
        fieldId: 'foPurifier1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 2 Current',
        fieldId: 'foPurifier2Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 2 Previous',
        fieldId: 'foPurifier2Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Purifier 2 Difference',
        fieldId: 'foPurifier2Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 1 Current',
        fieldId: 'erFan1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 1 Previous',
        fieldId: 'erFan1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 1 Difference',
        fieldId: 'erFan1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 2 Current',
        fieldId: 'erFan2Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 2 Previous',
        fieldId: 'erFan2Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 2 Difference',
        fieldId: 'erFan2Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 3 Current',
        fieldId: 'erFan3Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 3 Previous',
        fieldId: 'erFan3Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 3 Difference',
        fieldId: 'erFan3Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 4 Current',
        fieldId: 'erFan4Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 4 Previous',
        fieldId: 'erFan4Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'ER Fan 4 Difference',
        fieldId: 'erFan4Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    flowMeterInstallationOld: [
      {
        fieldLabel: 'FO Booster Unit Current',
        fieldId: 'foBoosterCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Booster Unit Previous',
        fieldId: 'foBoosterPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'FO Booster Unit Difference',
        fieldId: 'foBoosterDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Gas Oil Booster Unit Current',
        fieldId: 'gasOilBoosterCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Gas Oil Booster Unit Previous',
        fieldId: 'gasOilBoosterPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Gas Oil Booster Unit Difference',
        fieldId: 'gasOilBoosterDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Common Inlet Current',
        fieldId: 'meAeInletCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Common Inlet Previous',
        fieldId: 'meAeInletPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Common Inlet Difference',
        fieldId: 'meAeInletDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Inlet Current',
        fieldId: 'auxEngineInletCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Inlet Previous',
        fieldId: 'auxEngineInletPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Inlet Difference',
        fieldId: 'auxEngineInletDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Outlet Current',
        fieldId: 'auxEngineOutletCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Outlet Previous',
        fieldId: 'auxEngineOutletPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Engine Outlet Difference',
        fieldId: 'auxEngineOutletDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler Current',
        fieldId: 'auxBoiler1Curr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler Previous',
        fieldId: 'auxBoiler1Prev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Aux Boiler Difference',
        fieldId: 'auxBoiler1Diff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Cylinder Lube Oil Current',
        fieldId: 'cylinderLubeOilCurr',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Cylinder Lube Oil Previous',
        fieldId: 'cylinderLubeOilPrev',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Cylinder Lube Oil Difference',
        fieldId: 'cylinderLubeOilDiff',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    shorePower: SHORE_POWER_FORM_FIELDS,
  },
  cargoDetails: {
    stowageDetails: [
      {
        fieldLabel: 'Cargo Type',
        fieldId: 'cargoGrade',
        isRequired: true,
        fieldType: FieldType.Select,
        fieldOptions: [],
      },
      {
        fieldLabel: 'BL Cargo Quantity',
        fieldId: 'blQuantity',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Total Quantity Loaded',
        fieldId: 'totalQuantityLoaded',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
  },
  consumption: {
    cylinderOilConsumption: [
      {
        fieldLabel: 'Actual Consumption KG',
        fieldId: 'oilKgConsumption',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'SLOC',
        fieldId: 'oilSloc',
        isRequired: false,
        isReadOnly: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
  },
  bunker: {
    fuelOilBunkerTable: [
      {
        fieldLabel: 'Label',
        fieldId: 'isoFuelGrade',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'quantityOrdered',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'quantityReceived',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'bdnFigures',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'density',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'lcv',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'sulphur',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'viscosity',
        isRequired: true,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'labSulphur',
        isRequired: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
      {
        fieldLabel: 'Label',
        fieldId: 'stableUntil',
        isRequired: false,
        fieldType: FieldType.Calendar,
      },
    ],
  },
  stock: {
    options: [
      {
        fieldLabel: 'Was there a tank sounding?',
        fieldId: 'tankSounding',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: undefined,
      },
    ],
    fuelWaterRob: [
      {
        fieldLabel: 'Bilge water',
        fieldId: 'bilgeWater',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'm³',
      },
      {
        fieldLabel: 'Discharged Sludge',
        fieldId: 'dischargedSludge',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'm³',
      },
      {
        fieldLabel: 'Discharged Bilge Water',
        fieldId: 'dischargedBilgeWater',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'm³',
      },
      {
        fieldLabel: 'Fresh Water',
        fieldId: 'freshWater',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'Distilled Water',
        fieldId: 'distilledWater',
        isRequired: false,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
      {
        fieldLabel: 'Tank Cleaning Chemical',
        fieldId: 'tankCleaningChemical',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'MT',
      },
    ],
    lubeOilRob: [
      {
        fieldLabel: 'ME Lube Oil System',
        fieldId: 'lubeOilSystem',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Liters',
      },
      {
        fieldLabel: 'Low BN Cylinder Oil',
        fieldId: 'lowBnCylinderOil',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Liters',
      },

      {
        fieldLabel: 'High BN Cylinder Oil',
        fieldId: 'highBnCylinderOil',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Liters',
      },
      {
        fieldLabel: 'AE Lube Oil',
        fieldId: 'aeLubeOil',
        isRequired: true,
        isReadOnly: false,
        fieldType: FieldType.Number,
        fieldUnit: 'Liters',
      },
    ],
  },
};
